import React, { useState, useEffect } from 'react';
import './shared/PanelStyles.css';
import API_BASE_URL from '../config/api.js';

export default function IntelligentPitchPanel() {
  const [pitchData, setPitchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackCount, setTrackCount] = useState(0);

  useEffect(() => {
    fetchPitchData();
  }, []);

  const fetchPitchData = async () => {
    try {
      // Fetch catalog data to show tracks available for pitching
      const catalogResponse = await fetch(`${API_BASE_URL}/catalog/analytics`);
      const catalogData = await catalogResponse.json();
      
      setTrackCount(catalogData.metrics?.totalTracks || 0);
      
      // Mock pitch data for now
      setPitchData({
        totalPitches: 15,
        successRate: 23.8,
        activeCampaigns: 3,
        recentPitches: [
          { track: "999 Emotions", target: "Netflix Music Supervisor", status: "pending", date: "2025-08-12" },
          { track: "Okina & Sakana", target: "Apple Music Sync", status: "accepted", date: "2025-08-11" },
          { track: "Emotional Departure", target: "Spotify Editorial", status: "reviewing", date: "2025-08-10" }
        ]
      });
    } catch (error) {
      console.error('Error fetching pitch data:', error);
      setTrackCount(0);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="professional-loading">
        Loading pitch analytics...
      </div>
    );
  }

  return (
    <div className="professional-container">
      <div className="professional-header">
        <h1>🎯 Intelligent Pitch System</h1>
        <p>AI-powered music placement and sync opportunities</p>
      </div>

      {/* Overview Metrics */}
      <div className="professional-grid-4">
        <div className="professional-metric-card">
          <div className="professional-metric-value">{pitchData?.totalPitches || 0}</div>
          <div className="professional-metric-label">Total Pitches</div>
          <div className="professional-metric-change">All time submissions</div>
        </div>
        
        <div className="professional-metric-card">
          <div className="professional-metric-value">{pitchData?.successRate || 0}%</div>
          <div className="professional-metric-label">Success Rate</div>
          <div className="professional-metric-change positive">Above industry average</div>
        </div>
        
        <div className="professional-metric-card">
          <div className="professional-metric-value">{pitchData?.activeCampaigns || 0}</div>
          <div className="professional-metric-label">Active Campaigns</div>
          <div className="professional-metric-change">Currently running</div>
        </div>
        
        <div className="professional-metric-card">
          <div className="professional-metric-value">{trackCount}</div>
          <div className="professional-metric-label">Available Tracks</div>
          <div className="professional-metric-change">Ready for pitching</div>
        </div>
      </div>

      {/* Recent Pitches */}
      <div className="professional-card">
        <div className="professional-card-header">
          <h3 className="professional-card-title">Recent Pitch Activity</h3>
        </div>
        <div className="professional-card-content">
          {pitchData?.recentPitches ? (
            <ul className="professional-list">
              {pitchData.recentPitches.map((pitch, index) => (
                <li key={index} className="professional-list-item">
                  <div className="professional-list-item-content">
                    <div className="professional-list-item-title">{pitch.track}</div>
                    <div className="professional-list-item-meta">{pitch.target} • {pitch.date}</div>
                  </div>
                  <div className={`professional-list-item-value ${
                    pitch.status === 'accepted' ? 'positive' : 
                    pitch.status === 'pending' ? 'warning' : ''
                  }`}>
                    {pitch.status === 'accepted' ? '✅' : 
                     pitch.status === 'pending' ? '⏳' : 
                     pitch.status === 'reviewing' ? '🔍' : '📄'} {pitch.status}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No recent pitch activity</p>
          )}
        </div>
      </div>

      {/* Smart Recommendations */}
      <div className="professional-card">
        <div className="professional-card-header">
          <h3 className="professional-card-title">🎯 Smart Recommendations</h3>
        </div>
        <div className="professional-card-content">
          <ul className="professional-list">
            <li className="professional-list-item">
              <div className="professional-list-item-content">
                <div className="professional-list-item-title">Netflix Sync Opportunity</div>
                <div className="professional-list-item-meta">Seeking emotional indie tracks for upcoming series</div>
              </div>
              <div className="professional-list-item-value">🎬 High Match</div>
            </li>
            <li className="professional-list-item">
              <div className="professional-list-item-content">
                <div className="professional-list-item-title">Spotify Playlist Pitch</div>
                <div className="professional-list-item-meta">"999 Emotions" matches "Indie Feels" playlist criteria</div>
              </div>
              <div className="professional-list-item-value">🎵 90% Match</div>
            </li>
            <li className="professional-list-item">
              <div className="professional-list-item-content">
                <div className="professional-list-item-title">Apple Brand Sync</div>
                <div className="professional-list-item-meta">Looking for uplifting instrumental pieces</div>
              </div>
              <div className="professional-list-item-value">🍎 Medium Match</div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
